// src/pages/StocksStillCheap.jsx
export default function StocksStillCheap() {
    return (
      <article className="prose prose-invert max-w-3xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold tracking-tight mb-2">
          Long Stocks, Long Dollar, Long Carry: Reading the Post-FOMC Setup
        </h1>
  
        <p>
          The Fed held again this week, and two governors dissented in favor
          of cutting. That split is the whole story right now — not because
          it signals disarray, but because it maps cleanly onto a real debate:
          does the Fed look through a one-time tariff-driven price bump, or
          does it get ahead of it? Powell's answer was deliberately nuanced.
          He's not ruling out looking through it — he wants to see the
          cumulative impact of the last three months play out first.
        </p>
  
        <h2 className="text-2xl font-bold">The Setup</h2>
        <ul>
          <li><strong>Near-term drag, one-time in nature:</strong> tariffs are running an estimated $250-300B annualized hit, roughly 1% of GDP. If corporates absorb it, that pressures stocks. If it passes to consumers, that pressures consumption. Either way, it's a one-time price adjustment, not a sustained inflation driver.</li>
          <li><strong>Soft patch odds:</strong> roughly 30% chance of a soft patch in H2 this year. Not a recession call — a muddle-through call, with capex reaccelerating into Q4 as banking deregulation kicks in.</li>
          <li><strong>2026 tailwinds stacking up:</strong> German fiscal expansion on defense, the domestic credit boom potential from the "big beautiful bill" combined with AI and robotics, and China holding its own pace on credit and fiscal expansion in the same technologies. Broadly synchronized global tailwind once the near-term tariff drag clears.</li>
        </ul>
  
        <h2 className="text-2xl font-bold">Currency, Not Rates</h2>
        <p>
          The more interesting move isn't in rates — it's in FX. Powell's
          wait-and-watch stance marked a real inflection in the dollar. USD/JPY
          crossed its 200-day moving average, and the euro lost roughly 300
          basis points of ground against the dollar in just a few sessions.
          FX tends to trend until there's a genuine phase shift, and this
          looks like one. The ECB had partly predicated its own cutting cycle
          on the Fed being easy — if the Fed holds firm instead, that
          relationship can reverse meaningfully.
        </p>
        <p>
          There's still a fair number of cuts priced into the rates curve out
          to end-2026. That looks like too many. Once a credit boom gets into
          its first innings, mortgage origination picks up, home prices
          follow, and inflation re-accelerates — which makes an aggressively
          easy Fed hard to square with the setup shaping up for next year.
        </p>
  
        <h2 className="text-2xl font-bold">Why Stocks Still Look Cheap</h2>
        <p>
          Bottoms-up, on trailing PE and equity risk premium, stocks look a
          little rich. Top-down is a different picture. The US government is
          running fiscal expansion of roughly 6% this year and 6-7% next —
          that alone should push real asset prices higher relative to
          currency debasement. Layer a productive credit boom on top,
          specifically capital being deployed into AI and robotics, and that
          appreciation concentrates disproportionately in US equities
          relative to other asset classes. From that lens, stocks aren't
          expensive — they're underpriced relative to what's being built on top of them.
        </p>
  
        <h2 className="text-2xl font-bold">The Positioning</h2>
        <ul>
          <li><strong>Long stocks</strong> — AI capex is already contributing to GDP today; AI deployment is the multi-year tailwind behind it.</li>
          <li><strong>Long the dollar</strong> — both the AI wave and a Fed that holds its ground support this, though a September cut would complicate the trade via crosscurrents (own American stocks, need dollars to do it).</li>
          <li><strong>Long carry</strong> — mortgage basis, US Treasuries on asset swap, short options on rates. Rates aren't going anywhere fast, and that stasis is itself the trade.</li>
        </ul>
  
        <h2 className="text-2xl font-bold">What to Watch in August</h2>
        <p>
          Thin summer liquidity is the recurring risk every year on this side
          of the pandemic — last year's version was the August 5th Japan
          wobble. Illiquid markets amplify moves that would otherwise be
          noise. Nothing structural changes that view, but it's worth
          respecting going into a low-volume month.
        </p>
  
        <div className="my-6">
          {/* Space for a relevant chart if you want one */}
        </div>
  
        <h2 className="text-2xl font-bold">Closing Thoughts</h2>
        <p>
          One-time tariff drag, a Fed genuinely split on whether to look
          through it, a dollar inflection that matters more than the rates
          story, and a credit-and-fiscal setup into 2026 that keeps favoring
          US equities top-down. Stay long stocks, long dollar, long carry —
          and watch for the thin-liquidity wobble that August tends to deliver.
        </p>
        <p>That's the whole post. Cheap on a top-down view, watching for the August air pocket.</p>
      </article>
    );
  }